import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CallWithAuth } from '../../../actions/apiAction';
import { Avatar } from '@mui/material';

const MODE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'chat', label: 'Chat' },
    { key: 'voice', label: 'Voice' },
    { key: 'video', label: 'Video' },
];

function timeAgo(iso) {
    if (!iso) return 'Never active';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
}

function formatMsgTime(iso) {
    return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

const ActiveConversation = () => {
    const navigator = useNavigate();

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messagesError, setMessagesError] = useState(null);
    const [modeFilter, setModeFilter] = useState('all');

    const getUsers = async () => {
        setLoadingUsers(true);
        setUsersError(null);
        try {
            const res = await CallWithAuth('GET', '/creator-dashboard/users');
            setUsers(res?.data ?? []);
        } catch (err) {
            console.error('Failed to load users', err);
            setUsersError('Could not load fans. Try again.');
        } finally {
            setLoadingUsers(false);
        }
    };
    function formatDuration(decimalMinutes) {
        // voice_min / video_min are decimal minutes → mm:ss (matches rest of app)
        const totalSeconds = Math.max(0, Math.round(Number(decimalMinutes) * 60) || 0);
        const mm = Math.floor(totalSeconds / 60);
        const ss = totalSeconds % 60;
        return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }
    const openConversation = async (user) => {
        setSelectedUser(user);
        setModeFilter('all');
        setModalOpen(true);
        setLoadingMessages(true);
        setMessagesError(null);
        try {
            const res = await CallWithAuth('GET', '/creator-dashboard/conversations/messages?user_id=' + user?.user_id);
            setMessages(res?.data ?? []);
        } catch (err) {
            console.error('Failed to load conversation', err);
            setMessagesError('Could not load this conversation.');
        } finally {
            setLoadingMessages(false);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
        setMessages([]);
    };

    useEffect(() => { getUsers(); }, []);

    const filteredMessages = modeFilter === 'all'
        ? messages
        : messages.filter((m) => m.mode === modeFilter);

    return (
        <>
            <Helmet><title>Active Conversations | TLK</title></Helmet>

            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => navigator(-1)}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <div className="centr-texts-div">
                        <h1 className="main-header-s1__title">Recent Conversations</h1>
                        <h4>View your fan conversations.</h4>
                    </div>
                    {/* <button type="button" className="transparent-btn">
                        <img src="/images/icons/info.svg" alt="" />
                    </button> */}
                    <div className="main-header-s1__actions"></div>
                </div>

                <div className="body-content">
                    {/* <div className="body-sub-header-filter-s1">
                        <h3>All Conversations</h3>
                    </div> */}

                    {usersError && <div className="error-text">{usersError}</div>}

                    <div className="dashboard-menu-xl-lists">
                        {loadingUsers && <div className="chart-loading">Loading…</div>}

                        {!loadingUsers && users.length === 0 && !usersError && (
                            <div className="empty-text">No conversations yet.</div>
                        )}

                        {!loadingUsers && users.map((u) => (
                            <div
                                key={u.user_id}
                                className="dashboard-menu-item clickable"
                                onClick={() => openConversation(u)}
                            >
                                <div className="icon-container user">
                                    <Avatar alt={u.name} src={u.avatar}>
                                        {u.name?.trim()?.charAt(0) || 'U'}
                                    </Avatar>
                                </div>
                                <div className="text-container">
                                    <div className="h4-with-time">
                                        <h4>{u.name?.trim() || 'Unnamed user'}</h4>
                                        <div className="time">Last active: {timeAgo(u.last_active_pacific)}</div>
                                    </div>
                                    <h5>{u.email || u.user_id}</h5>
                                    <p>
                                        {u.total_messages} texts
                                        {Number(u.voice_min) > 0 ? ` · ${formatDuration(u.voice_min)} voice` : ''}
                                        {Number(u.video_min) > 0 ? ` · ${formatDuration(u.video_min)} video` : ''}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div >

            {/* Conversation Modal */}
            {
                modalOpen && (
                    <div className="conv-modal-overlay" onClick={closeModal}>
                        <div className="conv-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="conv-modal-header">
                                <div className="conv-modal-user">
                                    <Avatar alt={selectedUser?.name} src={selectedUser?.avatar} sx={{ width: 36, height: 36 }}>
                                        {selectedUser?.name?.trim()?.charAt(0) || 'U'}
                                    </Avatar>
                                    <div>
                                        <h3>{selectedUser?.name?.trim() || 'Unnamed user'}</h3>
                                        <h5>{selectedUser?.email || selectedUser?.user_id}</h5>
                                    </div>
                                </div>
                                <button type="button" className="transparent-btn" onClick={closeModal}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                                    </svg>
                                </button>

                            </div>

                            <div className="mode-filter-tabs">
                                {MODE_FILTERS.map((m) => (
                                    <button
                                        key={m.key}
                                        type="button"
                                        className={`mode-tab ${modeFilter === m.key ? 'active' : ''}`}
                                        onClick={() => setModeFilter(m.key)}
                                    >
                                        {m.label}
                                    </button>
                                ))}
                            </div>

                            <div className="conv-modal-body">
                                {loadingMessages && <div className="chart-loading">Loading conversation…</div>}
                                {messagesError && <div className="error-text">{messagesError}</div>}

                                {!loadingMessages && !messagesError && filteredMessages.length === 0 && (
                                    <div className="empty-text">No messages for this filter.</div>
                                )}

                                {!loadingMessages && filteredMessages.map((m, idx) => (
                                    <div
                                        key={idx}
                                        className={`msg-bubble ${m.role === 'assistant' ? 'msg-assistant' : 'msg-user'}`}
                                    >
                                        <div className="msg-meta">
                                            <span className={`mode-badge mode-${m.mode}`}>{m.mode}</span>
                                            <span className="msg-time">{formatMsgTime(m.sent_at)}</span>
                                        </div>
                                        <p>{m.content}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default ActiveConversation;